/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Text } from '@/shared/ui';
import type { AgencyValuesType } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { cepMask, phoneMask } from '@/shared/utils/input-masks';
import {
  documentLabelKey, maskDocument, registrationNameKey, storedDocument,
} from './ProfileSection/registration-document';
import { LogoCard } from './ProfileSection/LogoCard';
import { ProfileEditDrawers } from './ProfileSection/ProfileEditDrawers';
import { BlockMid, FieldsBlock, SettingsBlock, type SettingsField } from './blocks';
import { withCopperOnlyForCountedFields } from '../utils/build-settings-pending';

/**
 * Builds the registration fields, masked as the drawers mask them.
 *
 * @param agency - The active agency.
 * @param t - Translation function.
 * @returns The registration fields, in display order.
 */
function buildRegistrationFields(agency: AgencyValuesType, t: TFunction): SettingsField[] {
  return withCopperOnlyForCountedFields([
    { key: 'name', label: t(registrationNameKey(agency.type)), value: agency.name },
    { key: 'cnpj', label: t(documentLabelKey(agency)), value: maskDocument(storedDocument(agency)) },
    { key: 'phone', label: t('agencyData.labels.telefone'), value: agency.phone ? phoneMask(agency.phone).masked : '' },
    { key: 'email', label: t('v2.profile.email'), value: agency.email },
    { key: 'site', label: t('agencyData.labels.site'), value: agency.site },
    { key: 'creci', label: t('v2.profile.creci'), value: agency.creci },
  ]);
}


/**
 * Joins the street line, keeping the complement optional.
 *
 * @param agency - The active agency.
 * @returns The street line, or an empty string when there is no street.
 */
function formatStreetLine(agency: AgencyValuesType): string {
  if (!agency.street) return '';
  const base = `${agency.street}, ${agency.number ?? ''}`.trim();
  return agency.complement ? `${base}, ${agency.complement}` : base;
}

/**
 * Builds the address fields.
 *
 * The address used to be one run-on paragraph with a line break in the middle.
 * As four labelled fields each part can be seen to be missing, which is what
 * lets the copper treatment work here at all.
 *
 * @param agency - The active agency.
 * @param t - Translation function.
 * @returns The address fields, in display order.
 */
function buildAddressFields(agency: AgencyValuesType, t: TFunction): SettingsField[] {
  const cityState = agency.city && agency.state ? `${agency.city}/${agency.state}` : agency.city ?? '';
  // Optional throughout: the address is edited as one unit in its own drawer,
  // so a half-filled address is one gap and not four. The pending band counts
  // the registration fields; these stay quiet rather than promising four chips
  // that the band would never raise.
  return [
    { key: 'street', label: t('v2.profile.fields.street'), value: formatStreetLine(agency), optional: true },
    { key: 'neighborhood', label: t('v2.profile.fields.neighborhood'), value: agency.neighborhood, optional: true },
    { key: 'city', label: t('v2.profile.fields.cityState'), value: cityState, optional: true },
    { key: 'postalCode', label: t('v2.profile.fields.postalCode'), value: agency.postal_code ? cepMask(agency.postal_code).masked : '', optional: true },
  ];
}

/**
 * The Perfil section: the agency's registration data, address, logo and
 * description, each in the one block pattern.
 *
 * Editing still happens in the drawers — this section shows the values and
 * hands off; it does not grow a second way to change them.
 *
 * @returns The profile section element.
 */
export function ProfileSection() {
  const { t } = useTranslation('features/agency-settings');
  const { agency } = useAgency((state) => state);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [addressDrawerOpen, setAddressDrawerOpen] = useState(false);
  const [descriptionDrawerOpen, setDescriptionDrawerOpen] = useState(false);

  return (
    /* Paired two by two rather than stacked four deep. Every field here is a
       read-only line a few characters long, so a full-width block spends most
       of its row on nothing and pushes the last block under the fold — which
       is the opposite of what a settings screen is for: seeing the state of
       the account at a glance. */
    <div className="flex flex-col gap-(--space-4)">
      <div className="grid grid-cols-1 items-start gap-(--space-4) md:grid-cols-2">
        <SettingsBlock
          title={t('v2.profile.registrationData')}
          caption={t('v2.profile.registrationDescription')}
          action={{ label: t('v2.profile.edit'), onClick: () => setDrawerOpen(true), testId: 'profile-edit-registration' }}
          testId="profile-registration-block"
        >
          <FieldsBlock
            fields={buildRegistrationFields(agency, t)}
            missingLabel={t('v2.blocks.missingValue')}
            testIdPrefix="profile-field"
          />
        </SettingsBlock>

        <SettingsBlock
          title={t('v2.profile.address')}
          caption={t('v2.profile.addressDescription')}
          action={{ label: t('v2.profile.change'), onClick: () => setAddressDrawerOpen(true), testId: 'profile-edit-address' }}
          testId="profile-address-block"
        >
          <FieldsBlock
            fields={buildAddressFields(agency, t)}
            missingLabel={t('v2.blocks.missingValue')}
            testIdPrefix="profile-address-field"
          />
        </SettingsBlock>
      </div>

      <div className="grid grid-cols-1 items-start gap-(--space-4) md:grid-cols-2">
        <SettingsBlock
          title={t('v2.profile.visualIdentity')}
          caption={t('v2.profile.visualIdentityDescription')}
          testId="profile-logo-block"
        >
          <BlockMid>
            <LogoCard agencyId={agency.id ?? ''} logo={agency.logo} />
          </BlockMid>
        </SettingsBlock>

        <SettingsBlock
          title={t('v2.profile.description')}
          caption={t('v2.profile.descriptionExplanation')}
          action={{ label: t('v2.profile.edit'), onClick: () => setDescriptionDrawerOpen(true), testId: 'profile-edit-description' }}
          testId="profile-description-block"
        >
          <BlockMid>
            {/* Written but quiet, like the optional fields: the band does not
                count a missing description, so copper here would promise a
                chip that never arrives. */}
            <Text
              style={{
                margin: 0, fontSize: 'var(--text-body-sm)', lineHeight: 1.6,
                color: agency.description ? 'var(--text-secondary)' : 'var(--text-tertiary-soft)',
              }}
            >
              {agency.description || t('v2.profile.descriptionEmpty')}
            </Text>
          </BlockMid>
        </SettingsBlock>
      </div>

      <ProfileEditDrawers
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        addressDrawerOpen={addressDrawerOpen}
        setAddressDrawerOpen={setAddressDrawerOpen}
        descriptionDrawerOpen={descriptionDrawerOpen}
        setDescriptionDrawerOpen={setDescriptionDrawerOpen}
      />
    </div>
  );
}
